
import { useState } from "react";
import { ImageCropper } from "@/components/ImageCropper";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Profile } from "@/types/profile";

interface ProfileImageHandlerProps {
  user: { id: string };
  onUpdateProfile: (profile: Profile) => void;
  children: (props: {
    isUpdatingAvatar: boolean;
    handleProfilePictureUpload: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  }) => React.ReactNode;
}

export function ProfileImageHandler({ user, onUpdateProfile, children }: ProfileImageHandlerProps) {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result as string);
        setCropperOpen(true);
      };
      reader.readAsDataURL(file);
      
      // Clear the input value so the same file can be selected again
      event.target.value = '';
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCroppedImage = async (croppedImage: Blob) => {
    if (!user) return;

    try {
      setIsUpdatingAvatar(true);
      const timestamp = new Date().getTime();
      const filePath = `${user.id}/avatar_${timestamp}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('profile_pictures')
        .upload(filePath, croppedImage, {
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile_pictures')
        .getPublicUrl(filePath);

      const { error: updateError, data: updatedProfile } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id)
        .select('id, username, display_name, avatar_url, link_one, link_two, verification_status, is_creative')
        .single();

      if (updateError) throw updateError;

      // Update auth metadata to include new avatar_url
      await supabase.auth.updateUser({
        data: { avatar_url: publicUrl }
      });

      // Type assertion to handle the Supabase response
      onUpdateProfile(updatedProfile as Profile);
      toast.success('Profile picture updated successfully');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsUpdatingAvatar(false);
      setSelectedImage(null);
      setCropperOpen(false);
    }
  };

  return (
    <>
      {cropperOpen && selectedImage && (
        <ImageCropper
          image={selectedImage}
          onCrop={handleCroppedImage}
          isOpen={cropperOpen}
          onClose={() => {
            setCropperOpen(false);
            setSelectedImage(null);
          }}
        />
      )}
      {children({
        isUpdatingAvatar,
        handleProfilePictureUpload,
      })}
    </>
  );
}
